import { z } from "zod";

import { createPaginatedResponseSchema } from "../api/pagination";

import { type UserProfilePreview, UserProfilePreviewSchema } from "./preview";
import {
  type UserLikableImageMedia,
  UserLikableImageMediaSchema,
} from "./image";

import { parseAsUTC } from "@/utils/datetime";

export enum UserNotificationType {
  IMAGE_LIKE = "IMAGE_LIKE",
  WINK = "WINK",
  // IMAGE_COMMENT = "IMAGE_COMMENT",
  NEW_FOLLOWER = "NEW_FOLLOWER",
  FOLLOWING_IMAGE_UPLOAD = "FOLLOWING_IMAGE_UPLOAD", // Someone I followed uploaded an image
}

// IMAGE_LIKE
export const UserNotificationImageLikeDataItemsSchema = z.object({
  likedBy: UserProfilePreviewSchema,
  likedImage: UserLikableImageMediaSchema,
});

export type UserNotificationImageLikeDataItems = z.infer<
  typeof UserNotificationImageLikeDataItemsSchema
>;

export type UserNotificationImageLikeData = {
  imageLike: UserNotificationImageLikeDataItems;
};

export const UserNotificationImageLikeDataSchema = z.object({
  imageLike: UserNotificationImageLikeDataItemsSchema,
});

// WINK
export type UserNotificationWinkDataItems = {
  sentBy: UserProfilePreview;
};

export const UserNotificationWinkDataItemsSchema = z.object({
  sentBy: UserProfilePreviewSchema,
});

export type UserNotificationWinkData = {
  winkSent: UserNotificationWinkDataItems;
};

export const UserNotificationWinkDataSchema = z.object({
  winkSent: UserNotificationWinkDataItemsSchema,
});

// NEW_FOLLOWER
export type UserNotificationNewFollowerDataItems = {
  isFollowing: boolean;
  followedBy: UserProfilePreview;
};

export const UserNotificationNewFollowerDataItemsSchema = z.object({
  isFollowing: z.boolean(),
  followedBy: UserProfilePreviewSchema,
});

export type UserNotificationNewFollowerData = {
  newFollower: UserNotificationNewFollowerDataItems;
};

export const UserNotificationNewFollowerDataSchema = z.object({
  newFollower: UserNotificationNewFollowerDataItemsSchema,
});

// FOLLOWING_IMAGE_UPLOAD
export type UserNotificationFollowingImageUploadDataItems = {
  uploadedBy: UserProfilePreview;
  uploadedImage: UserLikableImageMedia;
};

export const UserNotificationFollowingImageUploadDataItemsSchema = z.object({
  uploadedBy: UserProfilePreviewSchema,
  uploadedImage: UserLikableImageMediaSchema,
});

export type UserNotificationFollowingImageUploadData = {
  followingImageUpload: UserNotificationFollowingImageUploadDataItems;
};

export const UserNotificationFollowingImageUploadDataSchema = z.object({
  followingImageUpload: UserNotificationFollowingImageUploadDataItemsSchema,
});

/**
 * Union type representing all possible notification data structures.
 * The specific structure depends on the notification type.
 */
export type UserNotificationData =
  | UserNotificationImageLikeData
  | UserNotificationWinkData
  | UserNotificationNewFollowerData
  | UserNotificationFollowingImageUploadData;

export const UserNotificationDataSchema: z.ZodType<UserNotificationData> =
  z.union([
    UserNotificationImageLikeDataSchema,
    UserNotificationWinkDataSchema,
    UserNotificationNewFollowerDataSchema,
    UserNotificationFollowingImageUploadDataSchema,
  ]);

export type UserNotification = {
  id: string;
  isRead: boolean;
  createdAt: Date;
  type: UserNotificationType;
  data: UserNotificationData;
};

export const UserNotificationSchema = z
  .object({
    id: z.string(),
    isRead: z.boolean(),
    createdAt: z
      .string()
      .nonempty("Date string cannot be empty")
      .transform(parseAsUTC),
    type: z.nativeEnum(UserNotificationType),
    data: UserNotificationDataSchema,
  })
  .refine((data) => {
    switch (data.type) {
      case UserNotificationType.IMAGE_LIKE:
        return UserNotificationImageLikeDataSchema.safeParse(data.data).success;
      case UserNotificationType.WINK:
        return UserNotificationWinkDataSchema.safeParse(data.data).success;
      case UserNotificationType.NEW_FOLLOWER:
        return UserNotificationNewFollowerDataSchema.safeParse(data.data)
          .success;
      case UserNotificationType.FOLLOWING_IMAGE_UPLOAD:
        return UserNotificationFollowingImageUploadDataSchema.safeParse(
          data.data,
        ).success;
      default:
        return false;
    }
  });

export const PaginatedUserNotificationSchema = createPaginatedResponseSchema(
  UserNotificationSchema,
);
